import classnames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { emitTravelersClickGAEvent, emitTravelersSelectGAEvent } from 'utils/ga-event-helpers';
import { openInput } from '../../../../actions/baseActions';
import { setCountAdults, setCountChildren } from '../../../../actions/flightActions';
import FadeTransition from '../../Common/FadeTransition';
import CounterItem from '../../Flight/Passengers/CounterItem';

type PassengerCounterProps = {
	openedInput?: string | null;
	name: string;
	placeholder: string;
	countAdults: number;
	countChildren: number;
	openInput: (name: string | null) => void;
	setCountAdults: (count: number) => void;
	setCountChildren: (count: number) => void;
};

const PassengerCounter: FC<PassengerCounterProps> = (props) => {
	const { t } = useTranslation();
	const [dropDownOpen, setDropDownOpen] = useState(false);
	const countsAtOpen = useRef<{ adults: number; children: number } | null>(null);

	useEffect(() => {
		const isOpen = props.openedInput === props.name;
		if (isOpen && !dropDownOpen) {
			// Opening: snapshot the counts so on close we can tell whether the user actually changed them.
			countsAtOpen.current = { adults: props.countAdults, children: props.countChildren };
			setDropDownOpen(true);
		} else if (!isOpen && dropDownOpen) {
			// Closing: only count a "selection" when the passenger counts actually changed while open.
			// Plain focus-loss / outside-click / opening another input must NOT emit a phantom selection.
			const snapshot = countsAtOpen.current;
			const changed =
				!!snapshot && (snapshot.adults !== props.countAdults || snapshot.children !== props.countChildren);
			if (changed) {
				emitTravelersSelectGAEvent({ adults: props.countAdults, children: props.countChildren });
			}
			setDropDownOpen(false);
		}
	}, [props.openedInput, props.name, dropDownOpen, props.countAdults, props.countChildren]);

	const getSelected = () => {
		const { countAdults, countChildren } = props;
		if (countAdults && countChildren) {
			return `${countAdults + countChildren} ${t('keywords.passengers')}`;
		}
		return `${countAdults} ${t('keywords.adults')}`;
	};

	const showItemsList = () => {
		props.openInput(props.name);
		emitTravelersClickGAEvent();
	};

	const handleAdultCountChange = (count: number) => {
		props.setCountAdults(count);
	};

	const handleChildCountChange = (count: number) => {
		props.setCountChildren(count);
	};

	const { countAdults, countChildren } = props;
	return (
		<div
			className={classnames('basic-drop-down', {
				'open-input': props.openedInput === props.name,
			})}
		>
			<div onClick={showItemsList}>
				<span className="d-name">{getSelected()}</span>
				<span className="d-icon">
					<i className="icon-arrow" />
				</span>
			</div>
			<FadeTransition timeout={1} in={props.openedInput === props.name}>
				<div className="body">
					<CounterItem onCountChange={handleAdultCountChange} count={countAdults} name={t('keywords.adults')} />
					<CounterItem onCountChange={handleChildCountChange} count={countChildren} name={t('keywords.children')} />
				</div>
			</FadeTransition>
		</div>
	);
};

// @ts-ignore
const mapStateToProps = (state) => ({
	openedInput: state.base.openedInput,
	countAdults: state.flight.countAdults,
	countChildren: state.flight.countChildren,
});

export default connect(mapStateToProps, { openInput, setCountChildren, setCountAdults })(PassengerCounter);
